import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { MobileShell } from "@/components/MobileShell";
import { Wordmark, Button, Field, Input } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ display_name: "", email: "", password: "", date_of_birth: "", invite_code: "" });
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!consent) { setError("Please confirm you are 18+ and accept the Terms and Privacy Policy."); return; }
    setLoading(true);
    try {
      await register({ ...form, accept_terms: true, accept_privacy: true });
      toast.success("Account created. Check your email to verify.");
      navigate("/onboarding");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MobileShell>
      <div className="px-7 pt-6 pb-10 min-h-screen flex flex-col">
        <button onClick={() => navigate("/welcome")} className="text-[#5F6D66] -ml-2 p-2 w-fit" data-testid="register-back-btn">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="mt-6 lg:mt-2">
          <Wordmark className="lg:hidden" />
          <h1 className="font-serif text-3xl text-[#1F2B26] mt-8 lg:mt-2">Create your account</h1>
          <p className="font-sans text-sm text-[#5F6D66] mt-2">A thoughtful space begins with a sincere start.</p>
        </div>

        <form onSubmit={submit} className="mt-7 space-y-4">
          <Field label="Invite code" hint="Sincerr is in private beta. Enter the code you were given.">
            <Input required value={form.invite_code} onChange={upd("invite_code")} placeholder="Your private-beta invite code" data-testid="register-invite-input" />
          </Field>
          <Field label="First name / display name">
            <Input required value={form.display_name} onChange={upd("display_name")} placeholder="How you'd like to be known" data-testid="register-name-input" />
          </Field>
          <Field label="Email">
            <Input type="email" autoComplete="email" required value={form.email} onChange={upd("email")} placeholder="you@example.com" data-testid="register-email-input" />
          </Field>
          <Field label="Date of birth" hint="You must be 18 or older. Only your age is ever shown.">
            <Input type="date" required value={form.date_of_birth} onChange={upd("date_of_birth")} data-testid="register-dob-input" />
          </Field>
          <Field label="Password" hint="At least 8 characters, with a letter and a number.">
            <Input type="password" autoComplete="new-password" required value={form.password} onChange={upd("password")} placeholder="Create a password" data-testid="register-password-input" />
          </Field>

          <button type="button" onClick={() => setConsent((c) => !c)} className="flex items-start gap-3 text-left w-full" data-testid="register-consent-toggle">
            <span className={`shrink-0 mt-0.5 h-5 w-5 rounded-md border flex items-center justify-center transition-colors ${consent ? "bg-[#253B35] border-[#253B35]" : "bg-white border-[#C9CFC9]"}`}>
              {consent && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
            </span>
            <span className="font-sans text-[13px] text-[#5F6D66] leading-relaxed">
              I confirm I am 18 or older and accept the Terms of Service and Privacy Policy.
            </span>
          </button>

          {error && <p className="font-sans text-sm text-[#9B4B45]" data-testid="register-error">{error}</p>}
          <Button type="submit" className="w-full" size="lg" disabled={loading} data-testid="register-submit-btn">
            {loading ? "Creating account…" : "Create account"}
          </Button>
        </form>

        <p className="mt-6 text-center font-sans text-sm text-[#5F6D66]">
          Already have an account?{" "}
          <button onClick={() => navigate("/login")} className="text-[#253B35] font-medium underline underline-offset-2" data-testid="register-to-login-link">Sign in</button>
        </p>
      </div>
    </MobileShell>
  );
}
